import { FC, useMemo, useState } from 'react';
import {
  Drawer,
  Stack,
  Box,
  IconButton,
  Typography,
  Chip,
  Divider,
  Tooltip,
  Button,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { dateFormat, SsccStatus, timeFormat } from '../../../common';
import { ReceiptEventPackageProps, SSCCProps } from '../../../datastore/ssccs';
import { ASNOccurrences, SsccActionsContext, SsccActionsPayload, SSCCOccurrences } from '../utils/types';
import { ManifestLegProps, ManifestSSCCProps } from '../../../datastore/tripSheets';
import { AsnStatus, TripLegStatus } from '../../../common/status';

interface SsccActionsDrawerProps {
  open: boolean;
  onClose: () => void;
  ssccs: SSCCProps[];
  ssccActionsContext: SsccActionsContext | null;
  ssccActionsPayload: SsccActionsPayload | null;
  asnOccurrences: ASNOccurrences;
  ssccOccurrences: SSCCOccurrences;
  handleOpenAddASNToSSCCDialog: () => void;
  handleOpenMarkCompletedSSCCDialog: () => void;
  handleOpenDeleteSSCCDialog: () => void;
  handleOpenDeleteASNDialog: (asn: ReceiptEventPackageProps) => void;
  getTimeBasedOnStatus: (params: { status: SsccStatus; sscc: SSCCProps | ManifestSSCCProps }) => string;
}

export const SsccActionsDrawer: FC<SsccActionsDrawerProps> = ({
  open,
  onClose,
  ssccs,
  ssccActionsContext,
  ssccActionsPayload,
  asnOccurrences,
  ssccOccurrences,
  handleOpenAddASNToSSCCDialog,
  handleOpenMarkCompletedSSCCDialog,
  handleOpenDeleteSSCCDialog,
  handleOpenDeleteASNDialog,
  getTimeBasedOnStatus
}) => {
  type AsnSortKey = 'asn' | 'po' | 'status' | 'supplier' | 'dateReceived';

  const [asnSortKey, setAsnSortKey] = useState<AsnSortKey>('asn');

  const livePayloadSscc = useMemo(
    () => (ssccActionsPayload?.ssccId ? ssccs.find((item) => item.id === ssccActionsPayload.ssccId) : undefined),
    [ssccActionsPayload?.ssccId, ssccs]
  );

  const ssccDetails = ssccActionsContext?.ssccEntry?.sscc || livePayloadSscc || ssccActionsPayload?.sscc;

  const ssccTypeLabel =
    ssccDetails?.ssccType || (ssccDetails?.isLargeItem ? 'Large' : ssccDetails ? 'Standard' : 'N/A');

  // Get ASNs from context or directly from SSCC details for unassigned SSCCs
  const ssccAsns = useMemo(
    () =>
      ssccActionsContext?.ssccEntry?.asns ||
      ssccDetails?.receiptedEvents
        ?.filter(({ packages }: { packages: ReceiptEventPackageProps[] }) => packages.length)
        .flatMap(({ packages }: { packages: ReceiptEventPackageProps[] }) => packages) ||
      [],
    [ssccActionsContext?.ssccEntry?.asns, ssccDetails?.receiptedEvents]
  );

  // Check if drawer is opened from unassigned SSCC (no trip/leg/manifest context)
  const isUnassignedSSCC =
    ssccActionsPayload && !ssccActionsPayload.tripId && !ssccActionsPayload.legId && !ssccActionsPayload.manifestId;

  const ssccActionsDisabled = isUnassignedSSCC
    ? false
    : !ssccActionsContext ||
      ![TripLegStatus.arrived, TripLegStatus.completed].includes(ssccActionsContext.tripDisplayStatus);

  const addAsnDisabled = isUnassignedSSCC || ssccActionsDisabled;

  const deleteSsccDisabled = isUnassignedSSCC
    ? true
    : !ssccActionsContext || ssccActionsContext.tripDisplayStatus !== TripLegStatus.arrived;

  const sortedSsccAsns = useMemo(() => {
    const getSortValue = (asn: ReceiptEventPackageProps) => {
      switch (asnSortKey) {
        case 'asn':
          return (asn.asn || '').toLowerCase();
        case 'po':
          return (asn.po || '').toLowerCase();
        case 'status':
          return (asn.status || '').toLowerCase();
        case 'supplier':
          return (asn.supplierName || '').toLowerCase();
        case 'dateReceived':
          return dayjs(asn.dateReceived).isValid() ? dayjs(asn.dateReceived).valueOf() : 0;
        default:
          return '';
      }
    };

    return [...(ssccAsns || [])].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return bValue - aValue;
      }

      return String(aValue).localeCompare(String(bValue));
    });
  }, [ssccAsns, asnSortKey]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          p: 2.5
        }
      }}>
      <Stack spacing={2.5} sx={{ height: '100%' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(25, 118, 210, 0.06)',
                border: '1px solid rgba(25, 118, 210, 0.12)'
              }}>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton size="small" onClick={onClose}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle1" fontWeight={600}>
                    SSCC Details
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {ssccActionsContext?.ssccEntry?.ssccId || ssccActionsPayload?.ssccId || 'N/A'}
                  </Typography>
                  <Chip
                    label={ssccTypeLabel}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(25, 118, 210, 0.12)',
                      color: '#1565c0'
                    }}
                  />
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={ssccDetails?.status || 'Unknown Status'}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                  {ssccDetails?.status && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {(() => {
                        const statusDate = getTimeBasedOnStatus({
                          status: ssccDetails.status as SsccStatus,
                          sscc: ssccDetails
                        });
                        return statusDate || 'N/A';
                      })()}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Box>

            {(() => {
              const ssccId = ssccActionsContext?.ssccEntry?.ssccId || ssccActionsPayload?.ssccId;
              const ssccDuplicateInfo = ssccId ? (ssccOccurrences.get(ssccId) ?? []) : [];
              const isSsccDuplicate = ssccDuplicateInfo.length > 1;

              return isSsccDuplicate ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(211, 47, 47, 0.06)',
                    border: '1px solid rgba(211, 47, 47, 0.2)'
                  }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ErrorOutlineIcon
                        sx={{
                          fontSize: 18,
                          color: '#c62828'
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#c62828',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                        Duplicate Detected • {ssccDuplicateInfo.length} occurrences
                      </Typography>
                    </Stack>
                    <Stack spacing={0.75}>
                      {ssccDuplicateInfo.map((info, ssccDuplicateInfoIndex) => (
                        <Chip
                          key={ssccDuplicateInfoIndex}
                          label={`Trip ${info.tripId || 'N/A'} • Leg ${
                            Number.isInteger(info.legIndex) ? (info.legIndex ?? 0) + 1 : 'N/A'
                          } • Manifest ${info.manifestId || 'N/A'}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            color: '#b71c1c',
                            border: '1px solid rgba(211, 47, 47, 0.25)',
                            justifyContent: 'flex-start',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              ) : null;
            })()}

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={1}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.65rem'
                    }}>
                    Nickname
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ssccDetails?.nickname || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.65rem'
                    }}>
                    Trip
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ssccActionsContext?.trip.reference || ssccActionsContext?.trip.id || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.65rem'
                    }}>
                    Leg
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ssccActionsContext
                      ? (() => {
                          const legIndex = ssccActionsContext.trip.legs.findIndex(
                            (l: ManifestLegProps) => l.id === ssccActionsContext.leg.id
                          );
                          return legIndex !== -1 ? `Leg ${legIndex + 1}` : 'N/A';
                        })()
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.65rem'
                    }}>
                    Manifest
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ssccActionsContext?.manifest.id || 'N/A'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.65rem'
                    }}>
                    Weight
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ssccDetails?.weight || 'N/A'} kg
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      fontSize: '0.65rem'
                    }}>
                    Dimensions (W x H x D)
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {ssccDetails?.width || 'N/A'} x {ssccDetails?.height || 'N/A'} x {ssccDetails?.depth || 'N/A'} cm
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  ASNs
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 125 }}>
                    <Select
                      value={asnSortKey}
                      onChange={(event) => setAsnSortKey(event.target.value as AsnSortKey)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root': {
                              fontSize: '0.72rem',
                              minHeight: 30,
                              py: 0.5
                            }
                          }
                        }
                      }}
                      sx={{
                        height: 24,
                        fontSize: '0.68rem',
                        '& .MuiSelect-select': {
                          fontSize: '0.68rem',
                          py: 0.25
                        },
                        '& .MuiSelect-icon': {
                          fontSize: '1rem'
                        }
                      }}>
                      <MenuItem value="asn">Sort: ASN</MenuItem>
                      <MenuItem value="po">Sort: PO</MenuItem>
                      <MenuItem value="status">Sort: Status</MenuItem>
                      <MenuItem value="supplier">Sort: Supplier</MenuItem>
                      <MenuItem value="dateReceived">Sort: Date Received</MenuItem>
                    </Select>
                  </FormControl>
                  <Chip
                    label={ssccAsns?.length || 0}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                </Stack>
              </Stack>
              {ssccAsns?.length ? (
                <Stack
                  spacing={1}
                  sx={{
                    mt: 1.5,
                    flex: 1,
                    overflowY: 'auto',
                    pr: 0.5,
                    minHeight: 0
                  }}>
                  {sortedSsccAsns.map((asn, asnIndex) => {
                    const asnDuplicateInfo = asn.asn ? (asnOccurrences.get(asn.asn) ?? []) : [];
                    const isAsnDuplicate = asnDuplicateInfo.length > 1;
                    const isAsnAvailable =
                      (asn.status || '').toString().trim().toLowerCase() === AsnStatus.available.toLowerCase();
                    const canShowDeleteAsnAction =
                      isAsnDuplicate ||
                      (!isAsnAvailable &&
                        !isUnassignedSSCC &&
                        !!ssccActionsContext &&
                        [TripLegStatus.arrived, TripLegStatus.completed].includes(
                          ssccActionsContext.tripDisplayStatus
                        ));

                    return (
                      <Box
                        key={`${asn.asn}-${asnIndex}`}
                        sx={{
                          borderRadius: 1,
                          backgroundColor: 'rgba(0,0,0,0.03)',
                          border: isAsnDuplicate ? '2px solid #d32f2f' : '1px solid rgba(0,0,0,0.08)'
                        }}>
                        {isAsnDuplicate && (
                          <Box
                            sx={{
                              backgroundColor: 'rgba(211, 47, 47, 0.06)',
                              borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
                              px: 1.5,
                              py: 0.75
                            }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <ErrorOutlineIcon
                                sx={{
                                  fontSize: 14,
                                  color: '#c62828'
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  color: '#c62828',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px'
                                }}>
                                Duplicate • {asnDuplicateInfo.length} locations
                              </Typography>
                            </Stack>
                            <Stack spacing={0.25} sx={{ mt: 0.75, pl: 2.5 }}>
                              {asnDuplicateInfo.map((info, idx) => (
                                <Typography
                                  key={`${info.tripId}-${info.legIndex}-${info.manifestId}-${idx}`}
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.65rem',
                                    color: '#b71c1c',
                                    lineHeight: 1.4
                                  }}>
                                  T{info.tripId || '?'} / L
                                  {Number.isInteger(info.legIndex) ? (info.legIndex || 0) + 1 : '?'} / M
                                  {info.manifestId || '?'} / S{info.ssccId || '?'}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        )}
                        <Box sx={{ px: 1.5, py: 1 }}>
                          <Stack spacing={1}>
                            {/* ASN and PO Row */}
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                <Stack sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    ASN
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{
                                      wordBreak: 'break-word',
                                      color: 'primary.main'
                                    }}>
                                    {asn.asn || 'N/A'}
                                  </Typography>
                                </Stack>
                                {asn.po && (
                                  <>
                                    <Divider orientation="vertical" flexItem />
                                    <Stack sx={{ minWidth: 0 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        PO
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        sx={{
                                          wordBreak: 'break-word',
                                          color: '#1976d2'
                                        }}>
                                        {asn.po}
                                      </Typography>
                                    </Stack>
                                  </>
                                )}
                              </Stack>
                              {canShowDeleteAsnAction && (
                                <Tooltip
                                  title={
                                    ssccActionsDisabled
                                      ? `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, please delete ASN in Bustle.`
                                      : ''
                                  }
                                  arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={ssccActionsDisabled}
                                      onClick={() => handleOpenDeleteASNDialog(asn)}
                                      sx={{
                                        color: '#d32f2f',
                                        '&:hover': {
                                          backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                        },
                                        '&.Mui-disabled': {
                                          color: 'rgba(0, 0, 0, 0.26)'
                                        }
                                      }}>
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Stack>

                            {/* Company, Status, and Date Row */}
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Chip
                                label={asn.status || 'Unknown Status'}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                              />
                              {asn.statusDate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
                                  {`${asn.statusDate?.format(dateFormat)} ${asn.statusDate?.format(timeFormat)}`}
                                </Typography>
                              )}
                            </Stack>

                            {/* Priority Row */}
                            {asn.priority && (
                              <Stack>
                                <Chip
                                  label={asn.priority}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    width: 'fit-content',
                                    borderColor:
                                      asn.priority === 'High'
                                        ? '#f44336'
                                        : asn.priority === 'Medium'
                                          ? '#ff9800'
                                          : '#2196f3'
                                  }}
                                />
                              </Stack>
                            )}

                            {/* Supplier Name Row */}
                            {asn.supplierName && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.7rem'
                                }}>
                                {asn.supplierName}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 1.5 }}>
                  No ASNs
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#ffffff',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            pt: 2
          }}>
          <Stack spacing={1}>
            <Tooltip
              title={
                addAsnDisabled
                  ? isUnassignedSSCC
                    ? 'You cannot add ASN to an unassigned SSCC, but you can delete non-available status ASNs from an assigned SSCC, then the ASN can be transferred here.'
                    : `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, please add SSCC in Bustle.`
                  : ''
              }
              arrow>
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={addAsnDisabled}
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={handleOpenAddASNToSSCCDialog}
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    borderColor: 'rgba(25, 118, 210, 0.6)',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}>
                  Add ASN to SSCC
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                ssccActionsDisabled
                  ? `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, you can not mark completed.`
                  : ''
              }
              arrow>
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={ssccActionsDisabled}
                  startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                  onClick={handleOpenMarkCompletedSSCCDialog}
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    borderColor: 'rgba(76, 175, 80, 0.6)',
                    color: '#4caf50',
                    '&:hover': {
                      borderColor: '#4caf50',
                      backgroundColor: 'rgba(76, 175, 80, 0.04)'
                    }
                  }}>
                  Mark SSCC Completed
                </Button>
              </span>
            </Tooltip>
            {!isUnassignedSSCC && (
              <Tooltip
                title={
                  deleteSsccDisabled
                    ? ssccActionsContext?.tripDisplayStatus === TripLegStatus.completed
                      ? 'Trip status is Completed, deleting SSCC is disabled.'
                      : `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, please delete SSCC in Bustle.`
                    : ''
                }
                arrow>
                <span>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={deleteSsccDisabled}
                    startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                    onClick={handleOpenDeleteSSCCDialog}
                    sx={{
                      height: 36,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      borderColor: 'rgba(211, 47, 47, 0.6)',
                      color: '#c62828',
                      '&:hover': {
                        borderColor: '#c62828',
                        backgroundColor: 'rgba(211, 47, 47, 0.06)'
                      }
                    }}>
                    Delete SSCC
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
};

SsccActionsDrawer.displayName = 'SsccActionsDrawer';
